import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { oktaAuth } from '../../auth/oktaConfig';
import { useAuth } from '../../auth/AuthContext';

export function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();

  // Redirect to dashboard if already authenticated with roles
  useEffect(() => {
    if (!auth.loading && auth.isAuthenticated && auth.roles.length > 0) {
      navigate('/admin', { replace: true });
    }
  }, [auth.loading, auth.isAuthenticated, auth.roles, navigate]);

  // Display messages based on URL params
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'session_expired') {
      setError('Your session has expired due to inactivity. Please sign in again.');
    } else if (reason === 'access_denied') {
      setError('You do not currently have access to the BRG application. If you believe this is an error, please contact your BRG administrator.');
    } else if (reason === 'okta_unavailable') {
      setError('Login is temporarily unavailable. Please try again in a few minutes or contact IT support if the issue persists.');
    } else if (reason === 'no_code') {
      setError('Authentication failed: Your user account may not be assigned to the BRG application. Please contact your administrator to request access.');
    }
  }, [searchParams]);

  const handleOktaLogin = async () => {
    setError(null);
    try {
      await oktaAuth.tokenManager.clear();
      
      await oktaAuth.signInWithRedirect();
    } catch (err) {
      console.error('[LoginPage] Login error:', err);
      setError('Login is temporarily unavailable. Please try again in a few minutes or contact IT support if the issue persists.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #0a1628 0%, #1a237e 30%, #4a148c 70%, #880e4f 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    }}>
      {/* Logo */}
      <div style={{ marginBottom: '32px' }}>
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.5 16.5c2.2-2 5.2-3.2 8.5-3.2 1.5 0 3 .25 4.3.7" stroke="white" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
          <path d="M38.5 18c2.5 2.8 4 6.5 4 10.5 0 1.8-.3 3.5-.8 5.1" stroke="white" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
          <path d="M38 37.5c-2.6 2.8-6.2 4.5-10.2 4.5-2.5 0-4.8-.6-6.8-1.8" stroke="white" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
          <path d="M16.5 36c-1.8-2.5-2.8-5.5-2.8-8.8 0-2.2.5-4.3 1.3-6.2" stroke="white" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
          {/* Arrow heads */}
          <polygon points="17,14 23,18.5 22.5,13" fill="white"/>
          <polygon points="41,15.5 36.5,20 42,20.5" fill="white"/>
          <polygon points="41,39 36,36 35.5,41" fill="white"/>
          <polygon points="17,39 22,36.5 19.5,41.5" fill="white"/>
        </svg>
      </div>

      {/* Login Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '48px 40px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 400,
          textAlign: 'center',
          color: '#1a1a2e',
          marginTop: 0,
          marginBottom: '32px',
          lineHeight: 1.3,
        }}>
          Welcome to<br />BRG
        </h1>

        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '24px',
            borderRadius: '6px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleOktaLogin}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '1rem',
            fontWeight: 600,
            color: 'white',
            backgroundColor: '#0a1628',
            border: 'none',
            borderRadius: '30px',
            cursor: 'pointer',
            marginBottom: '16px',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#1a237e')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0a1628')}
        >
          Sign In
        </button>

        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.875rem', color: '#666' }}>
          You will be redirected to Okta to sign in
        </div>
      </div>

      {/* Footer branding */}
      <div style={{
        marginTop: '40px',
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.85rem',
      }}>
        <span>an app by</span>
        <div style={{
          marginTop: '8px',
          fontSize: '1.8rem',
          fontWeight: 700,
          color: 'white',
          letterSpacing: '0.5px',
        }}>
          Freeman<span style={{ color: '#4fc3f7' }}>.</span>
        </div>
      </div>
    </div>
  );
}
